/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brand2, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  var collapseName = location.pathname.replace("/", "");
  if (collapseName === "modificarUsuario") {
    collapseName = "usuarios";
  }
  if (collapseName === "detalles") {
    collapseName = "solicitud";
  }
  if (collapseName === "detallesE") {
    collapseName = "expedientes";
  }
  let textColor = "warning";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const [sidebarWidth, setSidebarWidth] = useState(250); // valor inicial por defecto
  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    function handleEscalaZoom() {
      const escala = Math.round(window.devicePixelRatio * 100);
      const width = window.innerWidth;

      let nuevoSidebarWidth;

      if (width >= 800 && width <= 1920) {
        if (width >= 1800) nuevoSidebarWidth = 250;
        else if (width >= 1500) nuevoSidebarWidth = 230;
        else if (width >= 1200) nuevoSidebarWidth = 210;
        else if (width >= 1000) nuevoSidebarWidth = 190;
        else nuevoSidebarWidth = 170;

        // Ajuste fino por escala
        if (escala >= 175) nuevoSidebarWidth -= 40;
        else if (escala >= 150) nuevoSidebarWidth -= 30;
        else if (escala >= 125) nuevoSidebarWidth -= 20;
        // para escala menor a 125 no se resta nada
      } else {
        // Fuera de rango usa valor fijo
        nuevoSidebarWidth = 250;
      }

      setSidebarWidth(nuevoSidebarWidth);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();
    handleEscalaZoom();

    window.addEventListener("resize", handleMiniSidenav);
    window.addEventListener("resize", handleEscalaZoom);

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener("resize", handleMiniSidenav);
      window.removeEventListener("resize", handleEscalaZoom);
    };
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;
    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={"warning"}
          display={miniSidenav ? "none" : "block"}
          variant="caption"
          fontWeight="bold"
          fontSize={10}
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sidebarWidth={sidebarWidth}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center" paddingLeft="20px">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <MDBox component="img" src={brand} alt="Brand" width={miniSidenav ? "3rem" : "12rem"} />
          )}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      <MDBox sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List>{renderRoutes}</List>
      </MDBox>

      <MDBox pt={1} pb={1} px={4} textAlign="center" paddingLeft="20px">
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand2 && (
            <MDBox
              component="img"
              src={brand2}
              alt="Brand"
              width={miniSidenav ? "3rem" : "12rem"}
            />
          )}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brand2: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
